import React, { useEffect, useState } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { track } from "../../analytics/mixpanel"
import { PlanCard, IntervalToggle } from "./PlanCard"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

// Partner-facing pricing page (doc 10). Reads the plan catalog + current plan,
// renders shared PlanCards with a monthly/annual toggle, routes the CTA to Stripe
// Checkout. Owns the Pricing Viewed (mount) + Plan Selected (CTA) client events.
function CheckoutButton({ plan, interval, current, canManage, copy, checkoutPath }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)

  if (plan.key === current) {
    return <span className="block rounded-lg bg-gray-100 dark:bg-gray-800 px-4 py-2.5 text-center text-sm font-semibold text-gray-500 dark:text-gray-400">{copy.currentPlan}</span>
  }
  if (plan.free) return null

  const select = () => {
    track("Plan Selected", { plan: plan.key, interval })
    setBusy(true)
    setError(false)
    fetch(checkoutPath, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      body: JSON.stringify({ plan: plan.key, interval }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(({ url }) => { window.location.href = url })
      .catch(() => { setError(true); setBusy(false) })
  }

  return (
    <>
      <button
        type="button"
        onClick={select}
        disabled={busy || !canManage}
        title={canManage ? undefined : copy.manageHint}
        className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 ${
          plan.popular ? "bg-brand-500 text-gray-900 hover:bg-brand-400" : "bg-brand-600 text-white hover:bg-brand-700"
        }`}
      >
        {busy ? copy.selecting : copy.select}
      </button>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{copy.error}</p>}
    </>
  )
}

export default function PricingApp({ billingPath, checkoutPath, copy }) {
  const [data, setData] = useState(null)
  const [interval, setInterval] = useState("month")

  useEffect(() => {
    fetch(billingPath, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((d) => {
        setData(d)
        track("Pricing Viewed", { plans_shown: d.plans.length, interval })
      })
    // ponytail: fire once on mount; interval toggle is not a re-view.
  }, [billingPath]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null

  return (
    <ErrorBoundary>
      <header className="mb-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
      </header>

      <IntervalToggle interval={interval} setInterval={setInterval} copy={copy} />

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {data.plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            interval={interval}
            copy={copy}
            cta={
              <CheckoutButton
                plan={plan}
                interval={interval}
                current={data.current_plan}
                canManage={data.can_manage_billing}
                copy={copy}
                checkoutPath={checkoutPath}
              />
            }
          />
        ))}
      </div>

    </ErrorBoundary>
  )
}
